import type { SVGProps } from 'react';
import { ACCOUNT_PATHS } from './account-paths';
import { CONTROL_PATHS } from './control-paths';
import { MAP_PATHS } from './map-paths';
import { STATE_PATHS } from './state-paths';
import { TEAM_PATHS } from './team-paths';
import { WEATHER_PATHS } from './weather-paths';

/** One icon set only (rule 24): outline, 24 grid, stroke 1.7. Filled variants exist only for "selected". */
const CORE_PATHS = {
  dashboard: (
    <>
      <rect x="3.4" y="3.4" width="7.2" height="7.2" />
      <rect x="13.4" y="3.4" width="7.2" height="7.2" />
      <rect x="3.4" y="13.4" width="7.2" height="7.2" />
      <rect x="13.4" y="13.4" width="7.2" height="7.2" />
    </>
  ),
  operations: (
    <>
      <path d="M5.4 2.8h8.4l4.8 4.8v13.6H5.4z" />
      <path d="M13.8 2.8v4.8h4.8" />
    </>
  ),
  map: (
    <>
      <path d="M3.2 6.4 9 4.2v13.4l-5.8 2.2z" />
      <path d="M9 4.2 15 6.4v13.4L9 17.6" />
      <path d="M15 6.4 20.8 4.2v13.4L15 19.8" />
    </>
  ),
  teams: (
    <>
      <circle cx="8.6" cy="8.8" r="2.8" />
      <circle cx="16.6" cy="9.6" r="2.4" />
      <path d="M3.4 19.8a5.2 5.2 0 0 1 10.4 0M14.8 19.8a4.6 4.6 0 0 1 5.8-4.4" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="7.8" r="3.3" />
      <path d="M5.6 20.4a6.4 6.4 0 0 1 12.8 0" />
    </>
  ),
  resources: (
    <>
      <path d="M2.6 16.2V8.4h9.2v7.8" />
      <path d="M11.8 10.6h3.6l3.8 3.2v2.4" />
      <circle cx="6.3" cy="17.4" r="1.9" />
      <circle cx="15.8" cy="17.4" r="1.9" />
    </>
  ),
  devices: (
    <>
      <rect x="6.4" y="10.6" width="11.2" height="8.6" rx="1.6" />
      <circle cx="12" cy="14.9" r="1.6" />
      <path d="M12 10.6V6.4" />
      <path d="M9 4.6a4.2 4.2 0 0 1 6 0" />
    </>
  ),
  chat: (
    <path d="M20.4 14.8a2 2 0 0 1-2 2H8.6L4.6 20.4v-3.6a2 2 0 0 1-2-2V5.6a2 2 0 0 1 2-2h13.8a2 2 0 0 1 2 2Z" />
  ),
  timeline: (
    <>
      <path d="M5.6 3.4v17.2" />
      <rect x="4.1" y="6" width="3" height="3" />
      <rect x="4.1" y="15" width="3" height="3" />
      <path d="M10.4 7.5h9.4M10.4 16.5h6" />
    </>
  ),
  replay: (
    <>
      <circle cx="12" cy="12" r="8.8" />
      <path d="M10 8.6 16 12l-6 3.4z" />
    </>
  ),
  tracks: (
    <>
      <path d="M4 19.4 9 11l4 4 7-9" strokeDasharray="2.6 2.2" />
      <circle cx="4" cy="19.4" r="1.5" />
      <circle cx="20" cy="6" r="1.5" />
    </>
  ),
  report: (
    <>
      <path d="M5.4 2.8h8.4l4.8 4.8v13.6H5.4z" />
      <path d="M8.6 12.6h6.8M8.6 16.2h4.4" />
    </>
  ),
  drone: (
    <>
      <rect x="9.1" y="9.1" width="5.8" height="5.8" rx="1.8" />
      <circle cx="5.1" cy="5.1" r="2.5" />
      <circle cx="18.9" cy="5.1" r="2.5" />
      <circle cx="5.1" cy="18.9" r="2.5" />
      <circle cx="18.9" cy="18.9" r="2.5" />
    </>
  ),
  video: (
    <>
      <rect x="2.8" y="6.4" width="12.6" height="11.2" rx="1.6" />
      <path d="m15.4 10.6 5.8-3.2v9.2l-5.8-3.2" />
    </>
  ),
  phone: (
    <>
      <rect x="7.4" y="2.8" width="9.2" height="18.4" rx="2" />
      <path d="M10.6 18.4h2.8" />
    </>
  ),
  signal: (
    <>
      <circle cx="12" cy="18.4" r="1.4" fill="currentColor" stroke="none" />
      <path d="M8.4 14.8a5 5 0 0 1 7.2 0M5.6 11.8a9 9 0 0 1 12.8 0M2.8 8.8a13 13 0 0 1 18.4 0" />
    </>
  ),
  settings: (
    <>
      <path d="M3.4 7.2h9.2M16.6 7.2h4M3.4 12h3.2M10.6 12h10M3.4 16.8h7.2M14.6 16.8h6" />
      <circle cx="14.6" cy="7.2" r="2" />
      <circle cx="8.6" cy="12" r="2" />
      <circle cx="12.6" cy="16.8" r="2" />
    </>
  ),
  bell: (
    <>
      <path d="M18.4 15.4V10a6.4 6.4 0 1 0-12.8 0v5.4L4 17.8h16z" />
      <path d="M9.8 20.4a2.4 2.4 0 0 0 4.4 0" />
    </>
  ),
  qr: (
    <>
      <rect x="3.4" y="3.4" width="7" height="7" />
      <rect x="13.6" y="3.4" width="7" height="7" />
      <rect x="3.4" y="13.6" width="7" height="7" />
      <path d="M13.6 13.6h3v3h-3zM20.6 13.6v3M17.6 20.6h3" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21.2s7-5.8 7-11a7 7 0 1 0-14 0c0 5.2 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </>
  ),
  logout: (
    <>
      <path d="M9 4.4H5.2a1.6 1.6 0 0 0-1.6 1.6v12a1.6 1.6 0 0 0 1.6 1.6H9" />
      <path d="M14.6 16.4 19 12l-4.4-4.4M19 12H9.4" />
    </>
  ),
  building: (
    <>
      <path d="M4 20.4V5.6L12 3.2l8 2.4v14.8" />
      <path d="M2.8 20.4h18.4M9 9h2M13 9h2M9 12.6h2M13 12.6h2M9 16.2h2M13 16.2h2" />
    </>
  ),
} as const;

const PATHS = {
  ...CORE_PATHS,
  ...STATE_PATHS,
  ...CONTROL_PATHS,
  ...ACCOUNT_PATHS,
  ...WEATHER_PATHS,
  ...MAP_PATHS,
  ...TEAM_PATHS,
};

export type IconName = keyof typeof PATHS;

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  /** Filled rendering is reserved to the "selected" state. */
  filled?: boolean;
  title?: string;
}

export function Icon({ name, size = 15, filled = false, title, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title === undefined ? true : undefined}
      role={title === undefined ? undefined : 'img'}
      {...rest}
    >
      {title === undefined ? null : <title>{title}</title>}
      {PATHS[name]}
    </svg>
  );
}
